/**
 * FIXED DriversMap.jsx
 *
 * Key changes:
 * 1. Added "driver-status-changed" listener for IMMEDIATE status updates
 * 2. Fixed socket cleanup (removed socket.disconnect())
 * 3. Better state management for real-time updates
 * 4. ✅ Added verification_in_progress status support
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { getDrivers } from "../api/driverApi";

// 🛺 Auto images
import auto_offline from "../assets/images/auto_offline.png";
import auto_online from "../assets/images/auto_online.png";
import auto_accepted from "../assets/images/auto_accepted.png";
import auto_applied from "../assets/images/auto_applied.png";
import auto_waiting from "../assets/images/auto_waiting.png";
import auto_default from "../assets/images/auto.png";
import { socket } from "../socket";
import AccessControl from "../components/AccessControl";
import AdminBookRideModal from "./AdminBookRideModal";

const containerStyle = {
  width: "100%",
  height: "100vh",
};

// const center = {
//   lat: 11.0168,
//   lng: 76.9558,
// };

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// 🟢 Marker icon selector
const getMarkerIcon = (status) => {
  console.log(status, "<<< auto status");
  switch (status) {
    case "online":
      return auto_online;
    case "offline":
      return auto_offline;
    case "applied":
      return auto_applied;
    case "verification_in_progress":
      return auto_waiting;
    case "accepted":
      return auto_accepted;
    case "waiting":
      return auto_waiting;
    case "confirmed":
      return auto_accepted;
    default:
      return auto_default;
  }
};

// ✅ Status display label (short names)
const getStatusLabel = (status) => {
  switch (status) {
    case "verification_in_progress":
      return "Verifying";
    default:
      return status;
  }
};

// ✅ Status color
const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "green";
    case "offline":
      return "red";
    case "applied":
      return "#0077cc";
    case "verification_in_progress":
      return "#7C3AED";
    case "waiting":
      return "orange";
    case "approved":
      return "#0F6E56";
    case "accepted":
    case "confirmed":
      return "#B45309";
    case "rejected":
      return "#A32D2D";
    default:
      return "#666";
  }
};

// ✅ Safe driver mapper
const mapDriverData = (d) => ({
  ...d,
  latitude: d.location?.coordinates?.[1] ?? d.latitude,
  longitude: d.location?.coordinates?.[0] ?? d.longitude,
});

const DriversMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(socket.connected);
  const [bookRideOpen, setBookRideOpen] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const socketRef = useRef(null);

const [mapCenter, setMapCenter] = useState(() => {
  // Lazy init — zone coords from logged-in admin's localStorage
  try {
    const _a = localStorage.getItem("admin");
    const _d = _a ? JSON.parse(_a) : null;
    const _c = _d?.zone?.location?.coordinates;
    if (_c?.lat && _c?.lng) return { lat: _c.lat, lng: _c.lng };
  } catch {}
  return { lat: 11.0168, lng: 76.9558 }; // fallback
});

  // 🔁 Fetch ALL drivers from API
  const getAllDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDrivers();
      if (res.success) {
        const safeList = res.drivers
          .filter((d) => d.location?.coordinates)
          .map(mapDriverData);
        setDrivers(safeList);
      } else {

        console.error("Failed to fetch drivers:", res.error);
        // toast.error(res.error || "Failed to fetch drivers");
      }
    } catch (err) {
      console.error("getAllDrivers error:", err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔌 Socket connection
  useEffect(() => {
    getAllDrivers();

// ✅ Browser geolocation — highest priority
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        // Permission denied → zone coords already set in useState init, no action needed
        console.log("Geolocation denied — using zone coords");
      },
      { timeout: 5000 }
    );
  }

    socketRef.current = socket;

    // ✅ Connection status handlers
    const handleConnect = () => {
      console.log("🟢 Socket connected:", socket.id);
      setConnected(true);
      getAllDrivers(); // Refresh on reconnect
    };

    const handleDisconnect = (reason) => {
      console.log("🔴 Socket disconnected:", reason);
      setConnected(false);
    };

    // ✅ CRITICAL: Immediate status change handler
    const handleDriverStatusChanged = (data) => {
      console.log("🔄 driver-status-changed:", data);

      setDrivers((prev) =>
        prev.map((d) =>
          d.driverId === data.driverId ? { ...d, status: data.status } : d,
        ),
      );

      // Update selected driver if it's the one that changed
      setSelectedDriver((prev) =>
        prev?.driverId === data.driverId
          ? { ...prev, status: data.status }
          : prev,
      );
    };

    // ✅ Full drivers list handler
    const handleDriversListAll = (payload) => {
      console.log("drivers-list-all received:", payload);

      const safeList = (payload.drivers || [])
        .filter((d) => d.location?.coordinates || (d.latitude && d.longitude))
        .map(mapDriverData);

      setDrivers(safeList);

      if (payload.fareConfig) {
        console.log("Fare Config:", payload.fareConfig);
      }
    };

    // ✅ Live location update handler
    const handleDriverLocation = (data) => {
      console.log("driver-location:", data);
      setDrivers((prev) =>
        prev.map((d) =>
          d.driverId === data.driverId
            ? {
                ...d,
                latitude: data.latitude,
                longitude: data.longitude,
                ...(data.status ? { status: data.status } : {}),
              }
            : d,
        ),
      );
    };

    // Register all event listeners
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("driver-status-changed", handleDriverStatusChanged);
    socket.on("drivers-list-all", handleDriversListAll);
    socket.on("driver-location", handleDriverLocation);

    // Ride events that require full refetch
    const refetchEvents = [
      "ride-accepted",
      "ride-declined",
      "ride-cancelled",
      "trip_completed",
    ];
    refetchEvents.forEach((evt) => socket.on(evt, getAllDrivers));

    // ✅ Cleanup - DON'T disconnect, just remove listeners
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("driver-status-changed", handleDriverStatusChanged);
      socket.off("drivers-list-all", handleDriversListAll);
      socket.off("driver-location", handleDriverLocation);
      refetchEvents.forEach((evt) => socket.off(evt, getAllDrivers));
    };
  }, [getAllDrivers]);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setBookRideOpen(true)}
        style={{
          position: "absolute",
          top: 24,
          right: 150,
          zIndex: 999,
          background: "#2563eb",
          color: "#fff",
          border: "none",
          borderRadius: 50,
          padding: "14px 20px",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 4px 16px rgba(37,99,235,0.4)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        📞 Book Ride (Phone Call)
      </button>
    <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {/* 🔌 Connection status */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 999,
          background: connected
            ? "rgba(34, 197, 94, 0.9)"
            : "rgba(239, 68, 68, 0.9)",
          color: "#fff",
          padding: "6px 16px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#fff",
            animation: connected ? "pulse 2s infinite" : "none",
          }}
        />
        {connected ? "Live" : "Disconnected"}
      </div>

      {/* 🔄 Loading overlay */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            padding: "6px 16px",
            borderRadius: 20,
            fontSize: 13,
          }}
        >
          Loading drivers...
        </div>
      )}

      {/* 📊 Driver status summary */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 999,
          background: "rgba(255,255,255,0.95)",
          borderRadius: 10,
          padding: "8px 14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          fontSize: 12,
          lineHeight: "22px",
        }}
      >
        <div>
          🟢 Online:{" "}
          <b>{drivers.filter((d) => d.status === "online").length}</b>
        </div>
        <div>
          🔴 Offline:{" "}
          <b>{drivers.filter((d) => d.status === "offline").length}</b>
        </div>
        <div>
          🟡 Waiting:{" "}
          <b>{drivers.filter((d) => d.status === "waiting").length}</b>
        </div>
        <div>
          ⚫ Applied:{" "}
          <b>{drivers.filter((d) => d.status === "applied").length}</b>
        </div>
        <div>
          🔍 Verifying:{" "}
          <b>
            {
              drivers.filter((d) => d.status === "verification_in_progress")
                .length
            }
          </b>
        </div>
        <div>
          🛺 On Ride:{" "}
          <b>
            {
              drivers.filter(
                (d) => d.status === "accepted" || d.status === "confirmed",
              ).length
            }
          </b>
        </div>
        <div>
          📍 Total: <b>{drivers.length}</b>
        </div>
      </div>

      <LoadScript
        googleMapsApiKey={GOOGLE_KEY}
        libraries={["places"]}
        onLoad={() => setIsGoogleMapsLoaded(true)}
        onUnmount={() => setIsGoogleMapsLoaded(false)}
      >
        {isGoogleMapsLoaded && (
        <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={12}>
          {/* ✅ ALL drivers render */}
          {drivers.map((driver) => (
            <Marker
              key={driver.driverId}
              position={{ lat: driver.latitude, lng: driver.longitude }}
              icon={{
                url: getMarkerIcon(driver.status),
                ...(window.google?.maps?.Size
                  ? { scaledSize: new window.google.maps.Size(40, 20) }
                  : {}),
              }}
              onClick={() => setSelectedDriver(driver)}
            />
          ))}

          {/* 🪧 InfoWindow on click */}
          {selectedDriver && (
            <InfoWindow
              position={{
                lat: selectedDriver.latitude,
                lng: selectedDriver.longitude,
              }}
              onCloseClick={() => setSelectedDriver(null)}
            >
              <div style={{ textAlign: "center", width: 160 }}>
                <img
                  src={selectedDriver.photo}
                  alt={selectedDriver.name}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginBottom: 5,
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <h4 style={{ margin: 0 }}>{selectedDriver.name}</h4>
                <p style={{ margin: 0, fontSize: 12 }}>
                  {selectedDriver.vehicleNumber}
                </p>
                <p style={{ margin: 0, fontSize: 12 }}>
                  ⭐ {selectedDriver.avgRating || 4.5}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    fontWeight: "bold",
                    color: getStatusColor(selectedDriver.status),
                  }}
                >
                  ● {getStatusLabel(selectedDriver.status)}
                </p>
                {selectedDriver.upiId && (
                  <p style={{ margin: 0, fontSize: 12 }}>
                    UPI: {selectedDriver.upiId}
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
        )}
        <AdminBookRideModal
          isOpen={bookRideOpen}
          onClose={() => setBookRideOpen(false)}
          socket={socket}
        />
      </LoadScript>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default DriversMap;